import { useQuery } from "@tanstack/react-query";

import Sentry from "@similarweb/sentry-logger";

import { fetchService } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsCampaignUtils";

import { getProgressBarValue } from "../../../helpers/helpers";
import useTrafficSearchApiParams from "../../../hooks/useApiParamsSearch";
import type { ESourceType } from "../../../types";
import type {
    ITopNonBrandKeywordsResponse,
    ITopNonBrandKeywordsData,
    EPreset,
    ITopNonBrandedKeywordsQueryParams,
} from "../types";

const API_CONFIG = {
    queryKey: "search-overview-top-keywords",
    endpoint: "/api/searchoverview/overview/top-keywords",
};

const transformTopNonBrandedKeywordsData = (data: ITopNonBrandKeywordsResponse): ITopNonBrandKeywordsData => {
    if (!data) {
        return [];
    }
    return Object.entries(data).map(([searchTerm, details]) => ({
        searchTerm,
        clicks: {
            numberOfClicks: details.Clicks,
            shareOfClicks: details.ClicksShare,
        },
        yoyChange: { presentation: details.ChangePresentation, value: details.YoYChange },
        sitesClickShare: details.SitesClickShare ? { ...details.SitesClickShare } : undefined,
        percentageOf: {
            organic: getProgressBarValue(details.OrganicClicks, details.Clicks),
            paid: getProgressBarValue(details.PaidClicks, details.Clicks),
        },
    }));
};

async function fetchTopNonBrandedKeywordsData(params: ITopNonBrandedKeywordsQueryParams) {
    try {
        const response = await fetchService.get<ITopNonBrandKeywordsResponse>(API_CONFIG.endpoint, { ...params });
        return transformTopNonBrandedKeywordsData(response);
    } catch (error) {
        Sentry.error(`Error fetching top-non-branded-keywords data`, error);
    }
}

function useTopNonBrandedKeywordsTable({ sourceType, preset }: { sourceType?: ESourceType; preset?: EPreset }) {
    const { trafficSearchApiParams } = useTrafficSearchApiParams();
    const params = { ...trafficSearchApiParams, SourceType: sourceType, Preset: preset, pageSize: 5 };
    const { data, isLoading, isError } = useQuery({
        queryKey: [API_CONFIG.queryKey, params],
        queryFn: () => fetchTopNonBrandedKeywordsData(params),
        enabled: true,
    });
    return { data: data, isLoading, isError };
}

export default useTopNonBrandedKeywordsTable;
